/**
 * TeklifList — Teklif Modülü v2
 *
 * İki tipli (Taahhüt / Tedarikçi) teklif listesi.
 * - Üstte tip filtresi (Tümü / Taahhüt / Tedarikçi)
 * - İki ayrı "Yeni Teklif" butonu
 * - Durumlar: Taslak, Gönderildi, Onaylandı, Reddedildi
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { API, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  FileText, Plus, PencilSimple, TrashSimple, FilePdf, MicrosoftExcelLogo,
  MagnifyingGlass, Receipt, Package, Buildings,
} from "@phosphor-icons/react";

const STATUS_LABELS = {
  taslak: "Taslak",
  gonderildi: "Gönderildi",
  onaylandi: "Onaylandı",
  reddedildi: "Reddedildi",
};
const STATUS_TONES = {
  taslak: "bg-slate-100 text-slate-700",
  gonderildi: "bg-sky-100 text-sky-800",
  onaylandi: "bg-emerald-100 text-emerald-800",
  reddedildi: "bg-rose-100 text-rose-800",
};

const TYPE_LABELS = {
  taahhut: "Taahhüt",
  tedarikci: "Tedarikçi",
};
const TYPE_TONES = {
  taahhut: "bg-indigo-100 text-indigo-800 border-indigo-200",
  tedarikci: "bg-amber-100 text-amber-800 border-amber-200",
};

const fmtMoney = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso.length <= 10 ? iso + "T00:00:00Z" : iso);
    return d.toLocaleDateString("tr-TR");
  } catch { return iso; }
};
const curSym = (c) => ({ TRY: "TL", USD: "USD", EUR: "EUR" }[c] || "TL");

export default function TeklifList() {
  const nav = useNavigate();
  const askConfirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | taahhut | tedarikci

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/teklifler`);
      setItems(r.data?.items || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => ({
    all: items.length,
    taahhut: items.filter(t => (t.type || "taahhut") === "taahhut").length,
    tedarikci: items.filter(t => t.type === "tedarikci").length,
  }), [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((t) => {
      const tt = t.type || "taahhut";
      if (typeFilter !== "all" && tt !== typeFilter) return false;
      if (!q) return true;
      const label = tt === "tedarikci" ? (t.supplier_name || "") : (t.customer?.name || "");
      return (
        (t.number || "").toLowerCase().includes(q) ||
        label.toLowerCase().includes(q) ||
        (t.project_name || "").toLowerCase().includes(q)
      );
    });
  }, [items, search, typeFilter]);

  const downloadPdf = async (t) => {
    try {
      const r = await axios.get(`${API}/teklifler/${t.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const downloadExcel = async (t) => {
    try {
      const r = await axios.get(`${API}/teklifler/${t.id}/excel`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${t.number}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const deleteTeklif = async (t) => {
    const ok = await askConfirm({
      title: "Teklifi sil",
      description: `${t.number} nolu teklifi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`,
      confirmLabel: "Sil",
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/teklifler/${t.id}`);
      toast.success("Teklif silindi");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const FilterBtn = ({ value, label, icon: Icon, count }) => (
    <button
      onClick={() => setTypeFilter(value)}
      data-testid={`type-filter-${value}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition
        ${typeFilter === value
          ? "bg-[#1B4776] text-white border-[#1B4776]"
          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
    >
      {Icon && <Icon size={14} weight="bold" />}
      {label}
      <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${typeFilter === value ? "bg-white/20" : "bg-slate-100"}`}>
        {count}
      </span>
    </button>
  );

  return (
    <Shell>
      <div className="max-w-[1400px] mx-auto px-4 py-6 space-y-5" data-testid="teklif-list-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FileText size={26} weight="duotone" className="text-[#1B4776]" />
              Teklif Yönetimi
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Taahhüt ve tedarikçi tekliflerinizi tek yerden yönetin.
            </p>
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto flex-wrap">
            <div className="relative flex-1 md:w-72">
              <MagnifyingGlass size={16} weight="bold"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="No, müşteri/firma, proje ara…"
                className="pl-9"
                data-testid="teklif-search"
              />
            </div>
            <Button
              onClick={() => nav("/teklifler/yeni?type=taahhut")}
              className="bg-[#1B4776] hover:bg-[#163a63] text-white"
              data-testid="btn-new-taahhut"
            >
              <Plus size={16} className="mr-1.5" />
              Taahhüt Teklifi
            </Button>
            <Button
              onClick={() => nav("/teklifler/yeni?type=tedarikci")}
              variant="outline"
              className="border-[#1B4776] text-[#1B4776] hover:bg-slate-50"
              data-testid="btn-new-tedarikci"
            >
              <Plus size={16} className="mr-1.5" />
              Tedarikçi Teklifi
            </Button>
          </div>
        </div>

        {/* Type filter */}
        <div className="flex items-center gap-2" data-testid="type-filter-group">
          <FilterBtn value="all" label="Tümü" icon={FileText} count={counts.all} />
          <FilterBtn value="taahhut" label="Taahhüt" icon={Buildings} count={counts.taahhut} />
          <FilterBtn value="tedarikci" label="Tedarikçi" icon={Package} count={counts.tedarikci} />
        </div>

        {/* Table */}
        <Card className="overflow-hidden">
          <table className="w-full text-sm" data-testid="teklif-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Teklif No</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Tip</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Müşteri / Firma</th>
                <th className="text-center px-4 py-3 font-semibold text-slate-700">Tarih</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700">Tutar</th>
                <th className="text-center px-4 py-3 font-semibold text-slate-700">Durum</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700 w-48">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-slate-400">Yükleniyor…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-16">
                  <Receipt size={40} className="mx-auto text-slate-300 mb-3" weight="duotone" />
                  <div className="text-slate-500">Bu filtreye uygun teklif yok.</div>
                  <div className="mt-4 inline-flex gap-2">
                    <Button onClick={() => nav("/teklifler/yeni?type=taahhut")} className="bg-[#1B4776] hover:bg-[#163a63] text-white">
                      <Plus size={16} className="mr-1.5" /> Taahhüt Teklifi
                    </Button>
                    <Button onClick={() => nav("/teklifler/yeni?type=tedarikci")} variant="outline">
                      <Plus size={16} className="mr-1.5" /> Tedarikçi Teklifi
                    </Button>
                  </div>
                </td></tr>
              ) : filtered.map((t) => {
                const tt = t.type || "taahhut";
                const partyLabel = tt === "tedarikci"
                  ? (t.supplier_name || "—")
                  : (t.customer?.name || "—");
                return (
                  <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`teklif-row-${t.number}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-[#1B4776]">{t.number}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${TYPE_TONES[tt]}`}>
                        {TYPE_LABELS[tt]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{partyLabel}</div>
                      <div className="text-xs text-slate-500">{t.project_name || ""}</div>
                    </td>
                    <td className="text-center px-4 py-3 text-slate-600 text-xs">{fmtDate(t.teklif_date)}</td>
                    <td className="text-right px-4 py-3 font-mono text-sm font-semibold text-slate-900">
                      {fmtMoney(t.grand_total)} <span className="text-xs text-slate-500">{curSym(t.currency)}</span>
                    </td>
                    <td className="text-center px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_TONES[t.status] || STATUS_TONES.taslak}`}>
                        {STATUS_LABELS[t.status] || t.status}
                      </span>
                    </td>
                    <td className="text-right px-4 py-3">
                      <div className="inline-flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => downloadPdf(t)} title="PDF indir">
                          <FilePdf size={16} className="text-rose-600" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => downloadExcel(t)} title="Excel indir">
                          <MicrosoftExcelLogo size={16} className="text-emerald-700" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => nav(`/teklifler/${t.id}`)} title="Düzenle">
                          <PencilSimple size={14} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600 hover:bg-rose-50" onClick={() => deleteTeklif(t)} title="Sil">
                          <TrashSimple size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      </div>
    </Shell>
  );
}
